import React, { useEffect, useRef, useState } from "react"
import "./SchoolDataTable.scss"
import { useNavigate } from "react-router-dom"
import SchoolApi from "Api/SchoolApi"
import LogsApi from "Api/LogsApi"
import {
    Button,
    Col,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Spinner,
    Table,
    Input,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"
import { OverlayPanel } from "primereact/overlaypanel"
import LogsSidebar from "components/LogsSidebar/LogsSidebar"

const SchoolDataTable = () => {
    document.title = "Schools"
    const navigate = useNavigate("")
    const accessToken = localStorage.getItem("accessToken")
    const [schoolDetails, setSchoolDetails] = useState([])
    const userData = JSON.parse(localStorage.getItem("userData"))
    const [filteredSchoolDetails, setFilteredSchoolDetails] = useState([])
    const toast = useRef(null)
    const uploadbtn = useRef()
    const os = useRef(null)

    const [page, setPage] = useState(1)
    const [totalRecords, setTotalRecords] = useState(0)
    const [limit, setLimit] = useState(10)
    const [filterStatus, setFilterStatus] = useState("all")

    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "Schools", link: "#" },
        { label: "School List", link: "#" },
    ]

    const [loading, setLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState("")
    const [selectedSchools, setSelectedSchools] = useState([])
    const [excelLoader, setExcelLoader] = useState(false)

    // Logs state
    const [logsVisible, setLogsVisible] = useState(false)
    const [logs, setLogs] = useState([])
    const [allLogs, setAllLogs] = useState([])
    const [logsLoading, setLogsLoading] = useState(false)

    const toggleSelectAll = () => {
        if (selectedSchools.length === filteredSchoolDetails.length) {
            setSelectedSchools([])
        } else {
            setSelectedSchools(filteredSchoolDetails.map(school => school._id))
        }
    }

    const toggleSelection = schoolId => {
        setSelectedSchools(prevSchools =>
            prevSchools.includes(schoolId)
                ? prevSchools.filter(id => id !== schoolId)
                : [...prevSchools, schoolId],
        )
    }

    const handleUpdateStatus = async newStatus => {
        if (selectedSchools.length === 0) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Please select at least one School to update.",
                life: 3000,
            })
            return
        }

        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }

            const payload = {
                schoolIds: selectedSchools,
                newStatus: newStatus === "approve" ? "approved" : "rejected",
            }

            const response = await SchoolApi.updateSchoolStatus(payload, config)

            if (response.data.status) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Schools updated successfully",
                    life: 3000,
                })
                getAllSchoolDetails()
                setSelectedSchools([])
            }
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail:
                    error.response?.data?.message ||
                    "Failed to update School status",
                life: 3000,
            })
        }
    }

    const handleExcelUpload = () => {
        uploadbtn.current.click()
    }

    const handleFileChange = event => {
        setExcelLoader(true)
        const file = event.target.files[0]
        if (!file) return
        const formData = new FormData()
        formData.append("file", file)

        const config = {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }

        SchoolApi.uploadSchoolByExcel(formData, config)
            .then(res => {
                getAllSchoolDetails()
                event.target.value = ""
                setExcelLoader(false)
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: res.data.message,
                    life: 3000,
                })
            })
            .catch(err => {
                event.target.value = ""
                setExcelLoader(false)
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: err.response?.data?.message || "Failed to upload file",
                    life: 3000,
                })
            })
    }

    const handleSearch = e => {
        const value = e.target.value
        setSearchQuery(value)
    }

    const getAllSchoolDetails = () => {
        setLoading(true)
        const config = {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            params: {
                limit: limit,
                page: page,
            },
        }

        SchoolApi.getAllSchools(config)
            .then(response => {
                setLoading(false)
                setSchoolDetails(response.data.schools || [])
                setFilteredSchoolDetails(response.data.schools || [])
                setTotalRecords(response.data.totalRecords || 0)
            })
            .catch(error => {
                setLoading(false)
                setSchoolDetails([])
                setFilteredSchoolDetails([])
                setTotalRecords(0)
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: error.response?.data?.message || "Failed to fetch schools",
                    life: 3000,
                })
            })
    }

    useEffect(() => {
        getAllSchoolDetails()
    }, [accessToken, page, limit])

    // Logs functionality
    const fetchLogs = async () => {
        setLogsLoading(true)
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
            const response = await LogsApi.getLogsByServiceName(
                "school-service",
                config,
            )
            // Sort logs by timestamp in descending order
            const sortedLogs = response.data.logs.sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
            )
            setAllLogs(sortedLogs)
            setLogs(sortedLogs)
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch logs",
                life: 3000,
            })
        } finally {
            setLogsLoading(false)
        }
    }

    const handleLogsClick = () => {
        setLogsVisible(true)
        fetchLogs()
    }

    const handleFilterChange = level => {
        if (level === "all") {
            setLogs(allLogs)
        } else {
            setLogs(allLogs.filter(log => log.eventType.toLowerCase() === level))
        }
    }

    useEffect(() => {
        let filteredData = schoolDetails || []

        if (filterStatus !== "all") {
            filteredData = filteredData.filter(
                school => school.verifiedStatus === filterStatus,
            )
        }

        if (searchQuery.trim() !== "") {
            filteredData = filteredData.filter(school =>
                school.schoolName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                school.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                school.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                school.state?.toLowerCase().includes(searchQuery.toLowerCase())
            )
        }

        setFilteredSchoolDetails(filteredData)
    }, [filterStatus, schoolDetails, searchQuery])

    const totalPages = Math.ceil(totalRecords / limit)

    const handleDelete = async (schoolCode) => {
        if (window.confirm("Are you sure you want to delete this school?")) {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }

                const response = await SchoolApi.deleteSchool({ schoolCode }, config)

                if (response.data.status) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "School deleted successfully",
                        life: 3000,
                    })
                    getAllSchoolDetails()
                }
            } catch (error) {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: error.response?.data?.message || "Failed to delete school",
                    life: 3000,
                })
            }
        }
    }

    return (
        <div className="school-data-table">
            <Toast ref={toast} />
            <Row>
                <Col xs="12">
                    <div className="school-list-header d-flex align-items-center justify-content-between">
                        <CustomBreadCrumb data={breadcrumbItems} pageTitle="Schools" />

                        <div className="d-flex align-items-center gap-3">
                            {selectedSchools.length === 0 && (
                                <>
                                    <Input
                                        type="text"
                                        placeholder="Search School"
                                        className="form-control"
                                        style={{ width: "300px" }}
                                        value={searchQuery}
                                        onChange={handleSearch}
                                    />
                                    <input
                                        type="file"
                                        ref={uploadbtn}
                                        style={{ display: "none" }}
                                        onChange={handleFileChange}
                                        accept=".xlsx,.xls"
                                    />
                                    <Button
                                        type="button"
                                        color="success"
                                        className="waves-effect"
                                        style={{ minWidth: "40px" }}
                                        onClick={handleExcelUpload}
                                    >
                                        {excelLoader ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <i className="mdi mdi-microsoft-excel" />
                                        )}
                                    </Button>

                                    <Button
                                        type="button"
                                        color="info"
                                        className="waves-effect"
                                        style={{ minWidth: "100px" }}
                                        onClick={handleLogsClick}
                                    >
                                        <i className="mdi mdi-file-document-outline me-1" />
                                        Logs
                                    </Button>

                                    <Button
                                        type="button"
                                        color="secondary"
                                        className="waves-effect"
                                        style={{ minWidth: "120px" }}
                                        onClick={() => navigate("/addSchool")}
                                    >
                                        Add School
                                    </Button>
                                </>
                            )}

                            {selectedSchools.length > 0 && userData.role === "manager" && (
                                <>
                                    <Button
                                        type="button"
                                        color="primary"
                                        className="waves-effect"
                                        onClick={() => handleUpdateStatus("approve")}
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        type="button"
                                        color="danger"
                                        className="waves-effect"
                                        onClick={() => handleUpdateStatus("reject")}
                                    >
                                        Reject
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="school-data-table table-responsive mt-4 mb-3">
                        {loading ? (
                            <DataTableLoader cols={9} />
                        ) : (
                            <>
                                <Table className="table mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    onClick={toggleSelectAll}
                                                    checked={
                                                        selectedSchools.length ===
                                                        filteredSchoolDetails.length
                                                    }
                                                />
                                            </th>
                                            <th>#</th>
                                            <th>School Name</th>
                                            <th>Type</th>
                                            <th>City</th>
                                            <th>State</th>
                                            <th>Affiliation Board</th>
                                            <th>
                                                <span className="d-flex align-items-center gap-2 justify-content-center">
                                                    <span>Verified Status</span>
                                                    <span
                                                        onClick={e => os.current.toggle(e)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="mdi mdi-filter" />
                                                    </span>
                                                </span>
                                                <OverlayPanel ref={os}>
                                                    <Input
                                                        type="select"
                                                        value={filterStatus}
                                                        onChange={e => {
                                                            setFilterStatus(e.target.value)
                                                            os.current.hide()
                                                        }}
                                                        style={{ width: "150px" }}
                                                    >
                                                        <option value="all">All</option>
                                                        <option value="approved">Approved</option>
                                                        <option value="rejected">Rejected</option>
                                                        <option value="pending">Pending</option>
                                                    </Input>
                                                </OverlayPanel>
                                            </th>
                                            <th>Edit</th>
                                            <th>View</th>
                                            {/* {userData.role === "manager" && <th>Delete</th>} */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSchoolDetails && filteredSchoolDetails.map((school, index) => (
                                            <tr
                                                key={school._id}
                                                className="university-table-row"
                                            >
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedSchools.includes(
                                                            school._id,
                                                        )}
                                                        onClick={() => {
                                                            toggleSelection(school._id)
                                                        }}
                                                    />
                                                </td>
                                                <th scope="row">{(page - 1) * limit + index + 1}</th>
                                                <td>{school.schoolName || "N/A"}</td>
                                                <td>
                                                    {school.type && Array.isArray(school.type)
                                                        ? school.type.join(", ")
                                                        : school.type || "N/A"}
                                                </td>
                                                <td>{school.city || "N/A"}</td>
                                                <td>{school.state || "N/A"}</td>
                                                <td>
                                                    {school.affiliationBoard
                                                        ? Array.isArray(school.affiliationBoard)
                                                            ? school.affiliationBoard.join(", ")
                                                            : school.affiliationBoard
                                                        : "N/A"}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`status ${school.verifiedStatus?.toLowerCase()}`}
                                                    >
                                                        {school.verifiedStatus === "approved"
                                                            ? "Approved"
                                                            : school.verifiedStatus === "rejected"
                                                                ? "Rejected"
                                                                : "Pending"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className="edit-school"
                                                        onClick={() =>
                                                            navigate(`/edit-school/${school.schoolCode}`, { state: school })
                                                        }
                                                    >
                                                        <i className="ion ion-md-create" />
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className="view-school"
                                                        onClick={() =>
                                                            navigate(`/view-school/${school.schoolCode}`, { state: school })
                                                        }
                                                    >
                                                        <i className="ion ion-md-eye" />
                                                    </span>
                                                </td>
                                                {/* {userData.role === "manager" && (
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            color="danger"
                                                            onClick={() => handleDelete(school.schoolCode)}
                                                        >
                                                            <i className="mdi mdi-delete" />
                                                        </Button>
                                                    </td>
                                                )} */}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>

                                {filteredSchoolDetails.length === 0 && (
                                    <div className="text-center p-5">
                                        <p className="text-muted">No schools found</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {!loading && totalPages > 1 && (
                        <div className="pagination-container d-flex align-items-center justify-content-between mt-3">
                            <div>
                                <span className="text-muted">
                                    Showing {(page - 1) * limit + 1} to{" "}
                                    {Math.min(page * limit, totalRecords)} of {totalRecords}{" "}
                                    entries
                                </span>
                            </div>
                            <Pagination>
                                <PaginationItem disabled={page <= 1}>
                                    <PaginationLink
                                        previous
                                        onClick={() => setPage(page - 1)}
                                    />
                                </PaginationItem>

                                {/* First 3 pages */}
                                {[...Array(Math.max(0, Math.min(3, totalPages)))].map((_, index) => (
                                    <PaginationItem key={index} active={index + 1 === page}>
                                        <PaginationLink onClick={() => setPage(index + 1)}>
                                            {index + 1}
                                        </PaginationLink>
                                    </PaginationItem>
                                ))}

                                {/* Ellipsis before current page */}
                                {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                                    <PaginationItem disabled>
                                        <PaginationLink>...</PaginationLink>
                                    </PaginationItem>
                                )}

                                {/* Current page if in middle */}
                                {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                                    <PaginationItem active>
                                        <PaginationLink onClick={() => setPage(page)}>
                                            {page}
                                        </PaginationLink>
                                    </PaginationItem>
                                )}

                                {/* Ellipsis after current page */}
                                {totalPages > 6 && page < totalPages - 2 && (
                                    <PaginationItem disabled>
                                        <PaginationLink>...</PaginationLink>
                                    </PaginationItem>
                                )}

                                {/* Last 3 pages */}
                                {totalPages > 3 &&
                                    [...Array(Math.min(3, totalPages))].map((_, index) => {
                                        const pageNumber = totalPages - 2 + index
                                        return (
                                            <PaginationItem
                                                key={pageNumber}
                                                active={pageNumber === page}
                                            >
                                                <PaginationLink onClick={() => setPage(pageNumber)}>
                                                    {pageNumber}
                                                </PaginationLink>
                                            </PaginationItem>
                                        )
                                    })}

                                <PaginationItem disabled={page >= totalPages}>
                                    <PaginationLink next onClick={() => setPage(page + 1)} />
                                </PaginationItem>
                            </Pagination>
                        </div>
                    )}
                </Col>
            </Row>

            {/* Logs Sidebar */}
            <LogsSidebar
                visible={logsVisible}
                onHide={() => setLogsVisible(false)}
                logs={logs}
                loading={logsLoading}
                onRefresh={fetchLogs}
                onFilterChange={handleFilterChange}
                allLogs={allLogs}
            />
        </div>
    )
}

export default SchoolDataTable
